<template>
    <form class="row g-3" ref="form">
        <div v-if="$store.getters.selectedGatehouse?.settings?.guestbook_first_name_visible !== false" class="col-12 col-md-6">
            <label for="firstName" class="form-label">{{ $t("guestbook.firstName") }}</label>
            <input type="text" class="form-control" id="firstName" v-model="localGuestbook.firstName" maxlength="50" :required="firstNameRequired">
        </div>
        <div class="col-12 col-md-6">
            <label class="form-label required">{{ $t("guestbook.lastName") }}</label>
            <Multiselect
                v-if="$store.getters.selectedGatehouse.settings && $store.getters.selectedGatehouse.settings.guestbook_name_multiselect"
                ref="nameSelect"
                class="multiselect_custom"
                :model-value="localGuestbook.lastName"
                placeholder=""
                :options="nameOptions"
                @search-change="getGuestNames"
                @select="selectGuest"
                @remove="localGuestbook.lastName = ''"
                :custom-label="guestLabel"
                :searchable="true"
                :clear-on-select="false"
                :preserve-search="true"
                :allow-empty="true"
                :show-no-results="false"
                :taggable="true"
                @tag="addNewName"
                :track-by="null"
                :internal-search="false"
                :preselect-first="true"
                :select-label="$t('multiselect.pressToSelect')"
                :selected-label="$t('multiselect.selected')"
                :deselect-label="$t('multiselect.pressToDeselect')"
                :tagPlaceholder="$t('multiselect.guestName.tagPlaceholder')"
            >
                <template #option="{ option }">
                    <span v-if="option.isTag">{{ option.label }}</span>
                    <span v-else>
                        {{ guestLabel(option) }}
                        <small v-if="option.company" class="text-muted ms-1">{{ option.company }}</small>
                    </span>
                </template>
                <template #noOptions>
                    <span>{{ $t("multiselect.guestName.noOptions") }}</span>
                </template>
            </Multiselect>
            <input
                v-else
                type="text"
                class="form-control"
                id="lastName"
                v-model="localGuestbook.lastName"
                maxlength="50"
                required
            >
        </div>
        <div v-if="$store.getters.selectedGatehouse?.settings?.guestbook_phone_visible !== false" class="col-12 col-md-6">
            <label for="phone" class="form-label">{{ $t("guestbook.phone") }}</label>
            <input
                type="tel"
                class="form-control"
                id="phone"
                v-model="localGuestbook.phone"
                :required="phoneRequired"
                pattern="\+?[\d\s\-\/\.\(\)]{9,30}"
                :title="$t('guestbook.phoneFormat')"
            >
        </div>
        <div class="col-12 col-md-6">
            <label for="company" class="form-label">{{ $t("guestbook.company") }}</label>
            <input type="text" class="form-control" id="company" v-model="localGuestbook.company" maxlength="50" required>
        </div>
        <div class="col-12 col-md-6">
            <label for="cardNumber" class="form-label">{{ $t("guestbook.cardNumber") }}</label>
            <input type="text" class="form-control" id="cardNumber" v-model="localGuestbook.cardNumber" required>
        </div>
        <div class="col-12">
            <label for="cardType" class="form-label required">{{ $t("guestbook.cardType") }}</label>
            <div class="d-flex flex-wrap gap-2">
                <div
                    v-for="type in documentTypes"
                    :key="type.value"
                    class="status-block p-2 me-3 mb-3 mb-md-0 rounded text-center"
                    :class="{ 'selected': localGuestbook.cardType === type.value}"
                    @click="localGuestbook.cardType = type.value"
                >
                <div class="fs-6 fw-bold">{{ $t("documentType." + type.value) }}</div>
                <input
                    type="radio"
                    class="d-none"
                    :checked="localGuestbook.cardType === type.value"
                >
                </div>
            </div>
        </div>
        <div class="col-12 col-md-6">
            <label for="expectedFrom" class="form-label">{{ $t("guestbook.expectedFrom") }}</label>
            <input
                type="datetime-local"
                class="form-control"
                id="expectedFrom"
                v-model="localGuestbook.expectedFrom"
                :min="today"
                :class="{ 'is-invalid': isInvalidFrom }"
            >
        </div>
        <div class="col-12 col-md-6">
            <label for="expectedTo" class="form-label">{{ $t("guestbook.expectedTo") }}</label>
            <input
                type="datetime-local"
                class="form-control"
                id="expectedTo"
                v-model="localGuestbook.expectedTo"
                :min="localGuestbook.expectedFrom || today"
                :class="{ 'is-invalid': isInvalidDateRange }"
            >
        </div>
        <div class="col-12">
            <label for="status" class="form-label required">{{ $t("status.status") }}</label>
            <div class="d-flex flex-wrap gap-2">
                <div
                    v-for="status in statuses"
                    :key="status.id"
                    class="status-block p-2 me-3 mb-3 mb-md-0 rounded text-center"
                    :class="{ 'selected': localGuestbook.status === status.id}"
                    @click="localGuestbook.status = status.id"
                >
                    <icon v-if="status.icon" :icon="status.icon" class="status_icon" />
                    <div class="fs-6 fw-bold">{{ $t("status.guestbook." + status.name) }}</div>
                    <input
                        type="radio"
                        class="d-none"
                        :checked="localGuestbook.status === status.id"
                    >
                </div>
            </div>
        </div>
        <!-- with BOZP the visitor confirms it on the tablet before signing the arrival -->
        <div v-if="bozpEnabled === false" class="col-12 mt-5">
            <div class="form-check confirm-check">
                <input
                    class="form-check-input"
                    type="checkbox"
                    id="isTrained"
                    v-model="localGuestbook.isTrained"
                    :class="{ 'is-invalid': isTrainedInvalid}"
                >
                <label for="isTrained" class="form-check-label fw-bold fs-5 required">{{ $t("guestbook.isTrainedLabel") }}</label>

            </div>
            <div v-if="isTrainedInvalid" class="invalid-feedback d-block">
                {{ $t("guestbook.mustBeTrained") }}
            </div>
        </div>
        <div class="col-12"><p class="form-text mb-0">* {{ $t("utils.requiredFields") }}</p></div>
    </form>
</template>

<script>
    import Icon from '@/components/Icon.vue'
    import { formatDatetimeLocal } from '@/utils/date';
    import Multiselect from 'vue-multiselect'

    export default {
        components: {
            Icon,
            Multiselect,
        },
        emits: ["submit"],
        props: {
            guestbook: {
                type: Object,
                default: () => ({})
            }
        },
        data() {
            return {
                localGuestbook: {...this.guestbook},
                statuses: [],
                documentTypes: [],
                submitted: false,
                nameOptions: [],
                nameQuery: "",
                bozpEnabled: null,
            }
        },
        created() {
            this.getStatuses();
            this.getDocumentTypes();
            this.bozpStatusLoaded = this.getBozpStatus();
        },
        methods: {
            validateData() {
                if (!this.localGuestbook.lastName && this.$refs.nameSelect) {
                    this.$refs.nameSelect.$el.scrollIntoView({ behavior: "smooth", block: "center" });
                    this.$refs.nameSelect.$el.querySelector("input").focus();
                    return false;
                }
                if (this.$refs.form.checkValidity() === false){
                    this.$refs.form.reportValidity();
                    return false;
                }

                // null = BOZP status not loaded yet, the server would decide differently than the form
                if (this.bozpEnabled === null) {
                    return false;
                }
                if (!this.bozpEnabled && !this.localGuestbook.isTrained) {
                    return false;
                }

                return true;
            },
            async submitForm() {
                this.submitted = true;
                // the BOZP status decides whether the guard confirms the training, a click before it loads waits for it
                await this.bozpStatusLoaded;
                if (!this.validateData()) return;
                this.$emit("submit", this.localGuestbook)
            },
            getStatuses() {
                this.$store.getters.api.get(`/${this.$route.params.slug}/statuses?model=guestbook`)
                .then(response => {
                    this.statuses = response.data;

                    if (!this.localGuestbook.status) {
                        const reserved = this.statuses.find(s => s.name === "reserved");
                        if (reserved) {
                            this.localGuestbook.status = reserved.id;
                        }
                    }
                })
                .catch(error => {
                    console.error("Error loading statuses", error);
                });
            },
            getBozpStatus() {
                // asked live, a flag from login would be stale once the admin switches BOZP on or off
                return this.$store.getters.api.get(`/${this.$route.params.slug}/bozp/current`)
                .then(response => {
                    this.bozpEnabled = !!response.data.bozp;
                })
                .catch(error => {
                    console.error("Error loading BOZP", error);
                    this.bozpEnabled = false;
                });
            },
            getGuestNames(query) {
                this.nameQuery = query;

                if (!query || query.length < 2) {
                    this.nameOptions = [];
                    return;
                }

                this.$store.getters.api.get(`/${this.$route.params.slug}/guestbooks/guest-names`, { params: { q: query } })
                    .then(response => {
                        // responses can arrive out of order while typing, only the latest query counts
                        if (query === this.nameQuery) {
                            this.nameOptions = response.data;
                        }
                    })
                    .catch(err => {
                        console.error("Error fetching guest names", err);
                    });
            },
            addNewName(newName) {
                this.localGuestbook.lastName = newName;
            },
            selectGuest(guest) {
                this.localGuestbook.firstName = guest.first_name;
                this.localGuestbook.lastName = guest.last_name;
                this.localGuestbook.company = guest.company || this.localGuestbook.company;
                this.localGuestbook.phone = guest.phone || this.localGuestbook.phone;
            },
            guestLabel(guest) {
                // the selected value is the plain last name, the suggestions are guests from previous visits
                return typeof guest === "string" ? guest : `${guest.last_name} ${guest.first_name}`;
            },
            getDocumentTypes() {
                this.$store.getters.api.get("/document-types")
                .then(response => {
                    this.documentTypes = response.data;
                })
                .catch(error => {
                    console.error("Error loading document types", error);
                });
            },
        },
        computed: {
            firstNameRequired() {
                const settings = this.$store.getters.selectedGatehouse?.settings;
                return settings?.guestbook_first_name_visible !== false && !!settings?.guestbook_first_name_required;
            },
            phoneRequired() {
                const settings = this.$store.getters.selectedGatehouse?.settings;
                return settings?.guestbook_phone_visible !== false && !!settings?.guestbook_phone_required;
            },
            today() {
                const now = new Date();
                now.setSeconds(0);
                now.setMilliseconds(0);
                return now.toISOString().slice(0, 16);
            },
            isInvalidFrom() {
                if (!this.localGuestbook.expectedFrom) return false;

                return this.localGuestbook.expectedFrom < this.today;
            },
            isInvalidDateRange() {
                const from = new Date(this.localGuestbook.expectedFrom);
                const to = new Date(this.localGuestbook.expectedTo);
                return this.localGuestbook.expectedFrom && this.localGuestbook.expectedTo && to <= from;
            },
            isTrainedInvalid() {
                return this.submitted && !this.localGuestbook.isTrained
            }
        },
        watch: {
            guestbook: {
                handler(newVal) {
                    this.localGuestbook = {
                        firstName: newVal.first_name || "",
                        lastName: newVal.last_name || "",
                        phone: newVal.phone || "",
                        company: newVal.company || "",
                        cardNumber: newVal.personal_card_number || "",
                        cardType: newVal.personal_card_type || "",
                        expectedFrom: formatDatetimeLocal(newVal.expected_from) || "",
                        expectedTo: formatDatetimeLocal(newVal.expected_to) || "",
                        status: newVal.status || "",
                        isTrained: newVal.is_trained || false,
                    };

                    if (!this.localGuestbook.status && this.statuses.length) {
                        const reserved = this.statuses.find(s => s.name === "reserved");
                        if (reserved) {
                            this.localGuestbook.status = reserved.id;
                        }
                    }
                },
                immediate: true,
            },
            "localGuestbook.expectedFrom"(newVal) {
                if (!newVal) return;

                const from = new Date(newVal);
                const to = new Date(from.getTime() + 30 * 60 * 1000);

                if (!this.localGuestbook.expectedTo || this.localGuestbook.expectedTo < newVal) {
                    this.localGuestbook.expectedTo = formatDatetimeLocal(to);
                }
            }
        }
    }
</script>

<style scoped>
    .status_icon {
        width: 40px;
        height: 40px;
    }
</style>